import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projectsAPI, scansAPI, securityDashboardAPI, findingTicketAPI } from '../../services/api';
import { Project, Scan } from '../../types';
import LoadingSpinner from '../Common/LoadingSpinner';
import { LeafletGeoMap } from '../Charts';
import PriorityDashboard from './PriorityDashboard';

type DashboardView = 'overview' | 'priority';

interface TopIssue {
  id: number;
  scan_id: number;
  scan_name: string;
  project_id: number;
  project_name: string;
  host: string;
  ip: string;
  port: number;
  protocol: string;
  service: string;
  severity: string;
  title: string;
  description: string;
  vuln_type: string;
  created_at: string;
}

interface ProjectRiskCard {
  project_id: number;
  project_name: string;
  scan_id: number;
  scan_name: string;
  scan_date: string;
  risk_grade: string;
  risk_score: number;
  risk_color: string;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  total_findings: number;
  grade_description: string;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState<DashboardView>('overview');
  const [projects, setProjects] = useState<Project[]>([]);
  const [recentScans, setRecentScans] = useState<Scan[]>([]);
  const [criticalIssues, setCriticalIssues] = useState<TopIssue[]>([]);
  const [highIssues, setHighIssues] = useState<TopIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [riskCards, setRiskCards] = useState<ProjectRiskCard[]>([]);
  const [mttrMetrics, setMttrMetrics] = useState<{
    overall_mttr_hours: number;
    by_severity: Record<string, number>;
    sla_compliance_percent: number;
    total_tickets: number;
    open_tickets: number;
    resolved_tickets: number;
    sla_breached: number;
    tickets_at_risk: number;
    trend: { month: string; mttr_hours: number; resolved: number; sla_compliance: number }[];
  } | null>(null);

  const [allScans, setAllScans] = useState<Scan[]>([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [projectsRes, scansRes, topIssuesRes, riskCardsRes, mttrRes] = await Promise.all([
          projectsAPI.getAll(),
          scansAPI.getAll(),
          scansAPI.getTopIssues(),
          securityDashboardAPI.getProjectRiskCards().catch(() => ({ data: { cards: [] } })),
          findingTicketAPI.getMTTRMetrics().catch(() => ({ data: null }))
        ]);

        setProjects(projectsRes.data || []);
        // API returns {scans: [...], limit, page}, not an array directly
        const scansData = Array.isArray(scansRes.data) ? scansRes.data : (scansRes.data?.scans || []);
        setAllScans(scansData);
        setRecentScans(scansData.slice(0, 5));
        setCriticalIssues(topIssuesRes.data?.critical || []);
        setHighIssues(topIssuesRes.data?.high || []);
        setRiskCards(riskCardsRes.data?.cards || []);
        if (mttrRes.data) setMttrMetrics(mttrRes.data);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Calculate stats from ALL scans, not just recent ones
  const runningScans = allScans.filter(scan => scan.status === 'running');
  const pausedScans = allScans.filter(scan => scan.status === 'paused');
  const completedScans = allScans.filter(scan => scan.status === 'completed');
  const totalTargets = projects.reduce((sum, p) => sum + (p.targets?.length || 0), 0);

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return { bg: 'var(--color-critical-bg)', color: 'var(--color-critical)', border: 'var(--color-critical-border)' };
      case 'high': return { bg: 'var(--color-high-bg)', color: 'var(--color-high)', border: 'var(--color-high-border)' };
      case 'medium': return { bg: 'var(--color-medium-bg)', color: 'var(--color-medium)', border: 'var(--color-medium-border)' };
      case 'low': return { bg: 'var(--color-low-bg)', color: 'var(--color-low)', border: 'var(--color-low-border)' };
      default: return { bg: 'var(--color-surface-tertiary)', color: 'var(--color-text-muted)', border: 'var(--color-border-primary)' };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return { bg: 'var(--color-tint-blue)', color: 'var(--color-primary-700)' };
      case 'paused': return { bg: 'var(--color-tint-purple)', color: 'var(--color-purple-text)' };
      case 'completed': return { bg: 'var(--color-tint-green)', color: 'var(--color-low)' };
      case 'failed': return { bg: 'var(--color-tint-red)', color: 'var(--color-critical)' };
      case 'pending': return { bg: 'var(--color-tint-yellow)', color: 'var(--color-warning-text)' };
      default: return { bg: 'var(--color-surface-tertiary)', color: 'var(--color-text-muted)' };
    }
  };

  // Styles
  const styles = {
    container: {
      backgroundColor: 'var(--color-background-secondary)',
      minHeight: '100vh',
      padding: '0'
    },
    header: {
      backgroundColor: 'var(--color-background)',
      borderBottom: '1px solid var(--color-border-primary)',
      padding: '2rem',
      boxShadow: '0 1px 3px var(--color-card-shadow)'
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap' as const
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    title: {
      margin: '0',
      fontSize: '1.875rem',
      fontWeight: '700' as const,
      color: 'var(--color-text-primary)'
    },
    subtitle: {
      margin: '0.25rem 0 0',
      color: 'var(--color-text-tertiary)',
      fontSize: '0.875rem'
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap' as const
    },
    btnPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      backgroundColor: 'var(--color-primary-500)',
      color: 'var(--color-text-inverse)',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: '500' as const,
      cursor: 'pointer',
      transition: 'all 0.2s',
      textDecoration: 'none'
    },
    btnOutline: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      backgroundColor: 'var(--color-background)',
      color: 'var(--color-text-secondary)',
      border: '1px solid var(--color-border-secondary)',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: '500' as const,
      cursor: 'pointer',
      transition: 'all 0.2s',
      textDecoration: 'none'
    },
    content: {
      padding: '2rem'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    statCard: (color: string) => ({
      backgroundColor: 'var(--color-background)',
      borderRadius: '12px',
      border: '1px solid var(--color-border-primary)',
      padding: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      boxShadow: '0 1px 3px var(--color-card-shadow)',
      borderLeft: `4px solid ${color}`
    }),
    statIcon: (bgColor: string) => ({
      width: '52px',
      height: '52px',
      borderRadius: '12px',
      backgroundColor: bgColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      flexShrink: 0
    }),
    statInfo: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column' as const
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700' as const,
      color: 'var(--color-text-primary)',
      lineHeight: '1'
    },
    statLabel: {
      fontSize: '0.875rem',
      fontWeight: '500' as const,
      color: 'var(--color-text-tertiary)',
      marginTop: '0.25rem'
    },
    statSubtitle: {
      fontSize: '0.75rem',
      color: 'var(--color-text-muted)',
      marginTop: '0.25rem'
    },
    sectionCard: {
      backgroundColor: 'var(--color-background)',
      borderRadius: '12px',
      border: '1px solid var(--color-border-primary)',
      overflow: 'hidden',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 3px var(--color-card-shadow)'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1.25rem 1.5rem',
      backgroundColor: 'var(--color-background-secondary)',
      borderBottom: '1px solid var(--color-border-primary)'
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      margin: '0',
      fontSize: '1rem',
      fontWeight: '600' as const,
      color: 'var(--color-text-primary)'
    },
    sectionContent: {
      padding: '1.5rem'
    },
    projectsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1rem'
    },
    projectCard: {
      backgroundColor: 'var(--color-background-secondary)',
      borderRadius: '8px',
      border: '1px solid var(--color-border-primary)',
      padding: '1.25rem',
      transition: 'all 0.2s'
    },
    projectHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.75rem'
    },
    projectTitle: {
      margin: '0',
      fontSize: '1rem',
      fontWeight: '600' as const,
      color: 'var(--color-text-primary)'
    },
    projectDate: {
      fontSize: '0.75rem',
      color: 'var(--color-text-muted)'
    },
    projectDescription: {
      margin: '0 0 1rem 0',
      fontSize: '0.875rem',
      color: 'var(--color-text-tertiary)',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden'
    },
    projectStats: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1rem'
    },
    projectStat: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      fontSize: '0.75rem',
      color: 'var(--color-text-tertiary)'
    },
    projectActions: {
      display: 'flex',
      gap: '0.5rem'
    },
    btnSm: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.5rem 0.875rem',
      backgroundColor: 'var(--color-background)',
      color: 'var(--color-text-secondary)',
      border: '1px solid var(--color-border-secondary)',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: '500' as const,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    btnPrimarySm: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.5rem 0.875rem',
      backgroundColor: 'var(--color-primary-500)',
      color: 'var(--color-text-inverse)',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: '500' as const,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    scansTable: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    tableHeader: {
      textAlign: 'left' as const,
      padding: '0.75rem 1rem',
      fontSize: '0.75rem',
      fontWeight: '600' as const,
      color: 'var(--color-text-tertiary)',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      borderBottom: '1px solid var(--color-border-primary)'
    },
    tableCell: {
      padding: '1rem',
      fontSize: '0.875rem',
      color: 'var(--color-text-secondary)',
      borderBottom: '1px solid var(--color-background-tertiary)'
    },
    statusBadge: (status: string) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: getStatusColor(status).bg,
      color: getStatusColor(status).color,
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: '500' as const
    }),
    issuesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '1rem'
    },
    issueCard: (severity: string) => ({
      backgroundColor: 'var(--color-background-secondary)',
      borderRadius: '8px',
      border: '1px solid var(--color-border-primary)',
      borderLeft: `4px solid ${getSeverityColor(severity).color}`,
      padding: '1.25rem',
      transition: 'all 0.2s'
    }),
    issueHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.75rem'
    },
    severityBadge: (severity: string) => ({
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.5rem',
      backgroundColor: getSeverityColor(severity).bg,
      color: getSeverityColor(severity).color,
      borderRadius: '6px',
      fontSize: '0.625rem',
      fontWeight: '600' as const,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em'
    }),
    issueDate: {
      fontSize: '0.75rem',
      color: 'var(--color-text-muted)'
    },
    issueTitle: {
      margin: '0 0 0.75rem 0',
      fontSize: '0.875rem',
      fontWeight: '600' as const,
      color: 'var(--color-text-primary)',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden'
    },
    issueDetails: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.75rem',
      marginBottom: '0.75rem'
    },
    issueDetail: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      fontSize: '0.75rem',
      color: 'var(--color-text-tertiary)'
    },
    issueMeta: {
      display: 'flex',
      gap: '1rem',
      paddingTop: '0.75rem',
      borderTop: '1px solid var(--color-border-primary)'
    },
    issueMetaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      fontSize: '0.75rem',
      color: 'var(--color-text-tertiary)'
    },
    issueLink: {
      color: 'var(--color-primary-500)',
      textDecoration: 'none',
      transition: 'color 0.2s'
    },
    emptyState: {
      textAlign: 'center' as const,
      padding: '3rem 2rem',
      color: 'var(--color-text-tertiary)'
    },
    emptyIcon: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: 'var(--color-background-tertiary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1rem',
      fontSize: '1.5rem'
    },
    emptyText: {
      margin: '0 0 1rem 0',
      fontSize: '0.875rem'
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      backgroundColor: 'var(--color-background-secondary)'
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <LoadingSpinner text={t('common.loading', 'Loading...')} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header - hidden on mobile since Layout already shows page title */}
      <div style={styles.header} className="page-component-header">
        <div style={styles.headerTop}>
          <div style={styles.headerInfo}>
            <div>
              <h1 style={styles.title} className="page-component-title">{t('dashboard.title', 'Dashboard')}</h1>
              <p style={styles.subtitle}>
                {t('dashboard.welcomeMessage', "Welcome back! Here's your security overview.")}
              </p>
            </div>
          </div>
          <div style={styles.headerActions}>
            <Link to="/projects" style={styles.btnOutline}>
              <i className="fas fa-folder-open"></i> {t('projects.manageProjects', 'Manage Projects')}
            </Link>
            {projects.length > 0 && (
              <Link to={`/projects/${projects[0].id}/scans/new`} style={styles.btnPrimary}>
                <i className="fas fa-plus"></i> {t('scans.newScan', 'New Scan')}
              </Link>
            )}
          </div>
        </div>

        {/* View Toggle Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          padding: '0.5rem',
          background: 'var(--color-card-background)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: '8px',
          marginTop: '1rem'
        }}>
          <button
            onClick={() => setActiveView('overview')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: activeView === 'overview' ? 'var(--color-primary-500)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: activeView === 'overview' ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fas fa-chart-line"></i>
            {t('dashboard.overview', 'Overview')}
          </button>
          <button
            onClick={() => setActiveView('priority')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: activeView === 'priority' ? 'var(--color-primary-500)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: activeView === 'priority' ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fas fa-sort-amount-down"></i>
            {t('dashboard.priorityView', 'Priority')}
          </button>
        </div>
      </div>

      {/* Content */}
      {activeView === 'priority' ? (
        <div style={styles.content}>
          <PriorityDashboard />
        </div>
      ) : (
      <div style={styles.content}>
        {/* Stats Grid */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard('var(--color-primary-500)')}>
            <div style={styles.statIcon('var(--color-tint-blue)')}><i className="fas fa-folder-open" style={{ color: 'var(--color-primary-500)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{projects.length}</span>
              <span style={styles.statLabel}>{t('dashboard.totalProjects', 'Total Projects')}</span>
              {projects.length === 0 && (
                <span style={styles.statSubtitle}>{t('projects.createFirstProject', 'Create your first project')}</span>
              )}
            </div>
          </div>
          <div style={styles.statCard('var(--color-success)')}>
            <div style={styles.statIcon('var(--color-tint-green)')}><i className="fas fa-play" style={{ color: 'var(--color-success)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{runningScans.length + pausedScans.length}</span>
              <span style={styles.statLabel}>{t('dashboard.activeScans', 'Active Scans')}</span>
              <span style={styles.statSubtitle}>{runningScans.length} {t('scans.running', 'running').toLowerCase()}, {pausedScans.length} {t('scans.paused', 'paused').toLowerCase()}</span>
            </div>
          </div>
          <div style={styles.statCard('var(--color-accent-purple-light)')}>
            <div style={styles.statIcon('var(--color-tint-purple)')}><i className="fas fa-check-circle" style={{ color: 'var(--color-accent-purple-light)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{completedScans.length}</span>
              <span style={styles.statLabel}>{t('scans.completedScans', 'Completed Scans')}</span>
            </div>
          </div>
          <div style={styles.statCard('var(--color-warning)')}>
            <div style={styles.statIcon('var(--color-tint-yellow)')}><i className="fas fa-crosshairs" style={{ color: 'var(--color-warning)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{totalTargets}</span>
              <span style={styles.statLabel}>{t('dashboard.totalTargets', 'Total Targets')}</span>
              {totalTargets === 0 && (
                <span style={styles.statSubtitle}>{t('targets.addFirstTarget', 'Add your first target')}</span>
              )}
            </div>
          </div>
        </div>

        {/* MTTR & SLA Compliance Widget */}
        {mttrMetrics && mttrMetrics.total_tickets > 0 && (
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>
                <i className="fas fa-clock" style={{ color: 'var(--color-info)' }}></i>
                {t('dashboard.remediationMetrics', 'Remediation Metrics')}
              </h2>
              <Link to="/findings" style={{ fontSize: '0.8125rem', color: 'var(--color-primary-500)', textDecoration: 'none' }}>
                {t('dashboard.viewAllTickets', 'View all tickets')} <i className="fas fa-arrow-right" style={{ fontSize: '0.625rem' }}></i>
              </Link>
            </div>
            <div style={styles.sectionContent}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* MTTR Card */}
                <div style={{ padding: '1rem', borderRadius: '10px', backgroundColor: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-success)' }}>
                    {mttrMetrics.overall_mttr_hours > 0 ? (
                      mttrMetrics.overall_mttr_hours < 24
                        ? `${Math.round(mttrMetrics.overall_mttr_hours)}h`
                        : `${Math.round(mttrMetrics.overall_mttr_hours / 24)}d`
                    ) : '—'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>
                    {t('dashboard.avgMTTR', 'Avg. MTTR')}
                  </div>
                </div>
                {/* SLA Compliance Card */}
                <div style={{ padding: '1rem', borderRadius: '10px', backgroundColor: mttrMetrics.sla_compliance_percent >= 80 ? 'var(--color-low-bg)' : mttrMetrics.sla_compliance_percent >= 50 ? 'var(--color-medium-bg)' : 'var(--color-critical-bg)', border: `1px solid ${mttrMetrics.sla_compliance_percent >= 80 ? 'var(--color-low-border)' : mttrMetrics.sla_compliance_percent >= 50 ? 'var(--color-medium-border)' : 'var(--color-critical-border)'}`, textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: '700', color: mttrMetrics.sla_compliance_percent >= 80 ? 'var(--color-low)' : mttrMetrics.sla_compliance_percent >= 50 ? 'var(--color-medium)' : 'var(--color-critical)' }}>
                    {mttrMetrics.sla_compliance_percent > 0 ? `${Math.round(mttrMetrics.sla_compliance_percent)}%` : '—'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>
                    {t('dashboard.slaCompliance', 'SLA Compliance')}
                  </div>
                </div>
                {/* Open Tickets */}
                <div style={{ padding: '1rem', borderRadius: '10px', backgroundColor: 'var(--color-tint-blue)', border: '1px solid var(--color-info-border)', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-primary-600)' }}>
                    {mttrMetrics.open_tickets}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>
                    {t('dashboard.openTickets', 'Open Tickets')}
                  </div>
                </div>
                {/* SLA Breached */}
                <div style={{ padding: '1rem', borderRadius: '10px', backgroundColor: mttrMetrics.sla_breached > 0 ? 'var(--color-critical-bg)' : 'var(--color-background-secondary)', border: `1px solid ${mttrMetrics.sla_breached > 0 ? 'var(--color-critical-border)' : 'var(--color-border-primary)'}`, textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: '700', color: mttrMetrics.sla_breached > 0 ? 'var(--color-critical)' : 'var(--color-text-muted)' }}>
                    {mttrMetrics.sla_breached}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>
                    {t('dashboard.slaBreached', 'SLA Breached')}
                  </div>
                </div>
                {/* At Risk */}
                {mttrMetrics.tickets_at_risk > 0 && (
                  <div style={{ padding: '1rem', borderRadius: '10px', backgroundColor: 'var(--color-high-bg)', border: '1px solid var(--color-high-border)', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-high)' }}>
                      {mttrMetrics.tickets_at_risk}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>
                      {t('dashboard.atRisk', 'Due in 7 days')}
                    </div>
                  </div>
                )}
              </div>
              {/* MTTR by Severity */}
              {Object.keys(mttrMetrics.by_severity).length > 0 && (
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
                    {t('dashboard.mttrBySeverity', 'MTTR by Severity')}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {['critical', 'high', 'medium', 'low'].map(sev => {
                      const hours = mttrMetrics.by_severity[sev];
                      if (!hours) return null;
                      const colors: Record<string, { bg: string; text: string }> = {
                        critical: { bg: 'var(--color-critical-bg)', text: 'var(--color-critical)' },
                        high: { bg: 'var(--color-high-bg)', text: 'var(--color-high)' },
                        medium: { bg: 'var(--color-medium-bg)', text: 'var(--color-medium)' },
                        low: { bg: 'var(--color-low-bg)', text: 'var(--color-low)' },
                      };
                      const c = colors[sev] || { bg: 'var(--color-surface-tertiary)', text: 'var(--color-text-muted)' };
                      return (
                        <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem', borderRadius: '6px', backgroundColor: c.bg }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: c.text, textTransform: 'capitalize' }}>{sev}</span>
                          <span style={{ fontSize: '0.8125rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                            {hours < 24 ? `${Math.round(hours)}h` : `${Math.round(hours / 24)}d`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Project Risk Cards - A-F Grade per Project */}
        {riskCards.length > 0 && (
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>
                <i className="fas fa-shield-alt" style={{ color: 'var(--color-accent-purple)' }}></i>
                {t('dashboard.projectRiskLevels', 'Project Risk Levels')}
                <Link
                  to="/help?guide=gettingStarted&section=riskGrades"
                  title={t('common.learnMore', 'Learn more about risk grading')}
                  style={{
                    marginLeft: '0.5rem',
                    color: 'var(--color-text-muted)',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-accent-purple)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fas fa-question-circle"></i>
                </Link>
              </h2>
            </div>
            <div style={styles.sectionContent}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '1rem'
              }}>
                {riskCards.map((card) => (
                  <Link
                    key={card.project_id}
                    to={card.scan_id > 0 ? `/scans/${card.scan_id}` : `/projects/${card.project_id}`}
                    style={{
                      textDecoration: 'none',
                      display: 'block'
                    }}
                  >
                    <div style={{
                      backgroundColor: 'var(--color-background-secondary)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-border-primary)',
                      padding: '1.25rem',
                      display: 'flex',
                      gap: '1rem',
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-tertiary)';
                      e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
                      e.currentTarget.style.boxShadow = '0 4px 6px var(--color-card-shadow)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)';
                      e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    >
                      {/* Risk Grade Circle */}
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: card.risk_color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 2px 8px var(--color-card-shadow-hover)'
                      }}>
                        <span style={{
                          fontSize: '1.75rem',
                          fontWeight: '700',
                          color: 'var(--color-text-inverse)',
                          textShadow: '0 1px 2px var(--color-card-shadow)'
                        }}>
                          {card.risk_grade}
                        </span>
                      </div>

                      {/* Card Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                          margin: '0 0 0.25rem 0',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: 'var(--color-text-primary)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {card.project_name}
                        </h4>
                        <p style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.75rem',
                          color: 'var(--color-text-tertiary)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {card.scan_name}
                        </p>
                        <div style={{
                          fontSize: '0.75rem',
                          color: card.risk_color,
                          fontWeight: '500',
                          marginBottom: '0.5rem'
                        }}>
                          {card.grade_description}
                        </div>
                        {card.scan_id > 0 && (
                          <div style={{
                            display: 'flex',
                            gap: '0.75rem',
                            fontSize: '0.7rem',
                            color: 'var(--color-text-tertiary)'
                          }}>
                            {card.critical_count > 0 && (
                              <span style={{ color: 'var(--color-critical)' }}>
                                <i className="fas fa-exclamation-circle"></i> {card.critical_count}
                              </span>
                            )}
                            {card.high_count > 0 && (
                              <span style={{ color: 'var(--color-high)' }}>
                                <i className="fas fa-exclamation-triangle"></i> {card.high_count}
                              </span>
                            )}
                            {card.medium_count > 0 && (
                              <span style={{ color: 'var(--color-warning)' }}>
                                <i className="fas fa-info-circle"></i> {card.medium_count}
                              </span>
                            )}
                            <span style={{ marginLeft: 'auto' }}>
                              {card.scan_date ? new Date(card.scan_date).toLocaleString(undefined, {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Geographic Distribution - Show ALL targets user can see */}
        <div style={styles.sectionCard}>
          <div style={{ padding: '0' }}>
            <LeafletGeoMap
              showAllUserTargets={true}
              title={t('dashboard.targetGeoDistribution', 'Target Geographic Distribution')}
              showTable={true}
              height="400px"
            />
          </div>
        </div>

        {/* Recent Projects */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {t('dashboard.recentProjects', 'Recent Projects')}
            </h2>
            <Link
              to="/projects"
              style={styles.btnSm}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background)'; }}
            >
              <i className="fas fa-eye"></i> {t('common.viewAll', 'View All')}
            </Link>
          </div>
          <div style={styles.sectionContent}>
            {projects.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}><i className="fas fa-folder-open" style={{ color: 'var(--color-text-muted)' }}></i></div>
                <p style={styles.emptyText}>{t('projects.noProjects', 'No projects yet')}</p>
                <Link to="/projects" style={styles.btnPrimarySm}>
                  <i className="fas fa-plus"></i> {t('projects.createFirstProject', 'Create First Project')}
                </Link>
              </div>
            ) : (
              <div style={styles.projectsGrid}>
                {projects.slice(0, 4).map(project => (
                  <div
                    key={project.id}
                    style={styles.projectCard}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-tertiary)';
                      e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)';
                      e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                    }}
                  >
                    <div style={styles.projectHeader}>
                      <h3 style={styles.projectTitle}>{project.name}</h3>
                      <span style={styles.projectDate}>
                        {new Date(project.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {project.description && (
                      <p style={styles.projectDescription}>{project.description}</p>
                    )}
                    <div style={styles.projectStats}>
                      <span style={styles.projectStat}>
                        <i className="fas fa-crosshairs" style={{ color: 'var(--color-text-tertiary)' }}></i> {project.targets?.length || 0} {t('targets.title', 'targets').toLowerCase()}
                      </span>
                      <span style={styles.projectStat}>
                        <i className="fas fa-search" style={{ color: 'var(--color-text-tertiary)' }}></i> {project.scans?.length || 0} {t('scans.title', 'scans').toLowerCase()}
                      </span>
                    </div>
                    <div style={styles.projectActions}>
                      <Link to={`/projects/${project.id}`} style={styles.btnSm}>
                        <i className="fas fa-eye"></i> {t('common.view', 'View')}
                      </Link>
                      {(project.targets?.length || 0) > 0 && (
                        <Link to={`/projects/${project.id}/scans/new`} style={styles.btnPrimarySm}>
                          <i className="fas fa-play"></i> {t('scans.scanAction', 'Scan')}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Scans */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {t('dashboard.recentScans', 'Recent Scans')}
            </h2>
            <Link
              to="/scans"
              style={styles.btnSm}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background)'; }}
            >
              <i className="fas fa-eye"></i> {t('common.viewAll', 'View All')}
            </Link>
          </div>
          <div style={{ padding: recentScans.length === 0 ? '1.5rem' : '0' }}>
            {recentScans.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}><i className="fas fa-search" style={{ color: 'var(--color-text-muted)' }}></i></div>
                <p style={styles.emptyText}>{t('scans.noScans', 'No scans yet')}</p>
              </div>
            ) : (
              <table style={styles.scansTable}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>{t('scans.scanName', 'Scan Name')}</th>
                    <th style={styles.tableHeader}>{t('common.status', 'Status')}</th>
                    <th style={styles.tableHeader}>{t('scans.scanProgress', 'Progress')}</th>
                    <th style={styles.tableHeader}>{t('common.date', 'Date')}</th>
                    <th style={styles.tableHeader}>{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentScans.map(scan => (
                    <tr key={scan.id}>
                      <td style={styles.tableCell}>
                        <div style={{ fontWeight: '500', color: 'var(--color-text-primary)' }}>{scan.name}</div>
                      </td>
                      <td style={styles.tableCell}>
                        <span style={styles.statusBadge(scan.status)}>
                          <i className={
                            scan.status === 'running' ? 'fas fa-spinner fa-spin' :
                            scan.status === 'completed' ? 'fas fa-check-circle' :
                            scan.status === 'paused' ? 'fas fa-pause' :
                            scan.status === 'failed' ? 'fas fa-exclamation-circle' :
                            scan.status === 'pending' ? 'fas fa-clock' : 'fas fa-question-circle'
                          }></i>
                          {' '}{t(`scans.${scan.status}`, scan.status)}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        {scan.status === 'running' ? `${scan.progress || 0}%` : '-'}
                      </td>
                      <td style={styles.tableCell}>
                        {new Date(scan.created_at).toLocaleDateString()}
                      </td>
                      <td style={styles.tableCell}>
                        <Link
                          to={`/scans/${scan.id}`}
                          style={styles.btnSm}
                        >
                          <i className="fas fa-eye"></i> {t('common.view', 'View')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Critical Issues */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {t('dashboard.criticalIssues', 'Critical Issues')}
            </h2>
            <Link
              to="/results"
              style={styles.btnSm}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background)'; }}
            >
              <i className="fas fa-eye"></i> {t('common.viewAll', 'View All')}
            </Link>
          </div>
          <div style={styles.sectionContent}>
            {criticalIssues.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ ...styles.emptyIcon, backgroundColor: 'var(--color-tint-green)' }}><i className="fas fa-shield-alt" style={{ color: 'var(--color-success)' }}></i></div>
                <p style={styles.emptyText}>{t('dashboard.noCriticalIssues', 'No critical issues found')}</p>
              </div>
            ) : (
              <div style={styles.issuesGrid}>
                {criticalIssues.map((issue, index) => (
                  <div
                    key={index}
                    style={styles.issueCard(issue.severity)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-tertiary)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)';
                    }}
                  >
                    <div style={styles.issueHeader}>
                      <span style={styles.severityBadge(issue.severity)}>
                        {issue.severity}
                      </span>
                      <span style={styles.issueDate}>
                        {new Date(issue.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h4 style={styles.issueTitle}>{issue.title}</h4>
                    <div style={styles.issueDetails}>
                      <span style={styles.issueDetail}>
                        <i className="fas fa-server" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.host || issue.ip}
                      </span>
                      {issue.port > 0 && (
                        <span style={styles.issueDetail}>
                          <i className="fas fa-plug" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.port}/{issue.protocol}
                        </span>
                      )}
                      {issue.service && (
                        <span style={styles.issueDetail}>
                          <i className="fas fa-cog" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.service}
                        </span>
                      )}
                    </div>
                    <div style={styles.issueMeta}>
                      <span style={styles.issueMetaItem}>
                        <i className="fas fa-folder" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.project_name}
                      </span>
                      <Link
                        to={`/scans/${issue.scan_id}`}
                        style={{ ...styles.issueMetaItem, ...styles.issueLink }}
                      >
                        <i className="fas fa-search" style={{ color: 'var(--color-primary-500)' }}></i> {issue.scan_name}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* High Severity Issues */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {t('dashboard.highSeverityIssues', 'High Severity Issues')}
            </h2>
            <Link
              to="/results"
              style={styles.btnSm}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background)'; }}
            >
              <i className="fas fa-eye"></i> {t('common.viewAll', 'View All')}
            </Link>
          </div>
          <div style={styles.sectionContent}>
            {highIssues.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ ...styles.emptyIcon, backgroundColor: 'var(--color-tint-green)' }}><i className="fas fa-shield-alt" style={{ color: 'var(--color-success)' }}></i></div>
                <p style={styles.emptyText}>{t('dashboard.noHighSeverityIssues', 'No high severity issues found')}</p>
              </div>
            ) : (
              <div style={styles.issuesGrid}>
                {highIssues.map((issue, index) => (
                  <div
                    key={index}
                    style={styles.issueCard(issue.severity)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-tertiary)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-secondary)';
                    }}
                  >
                    <div style={styles.issueHeader}>
                      <span style={styles.severityBadge(issue.severity)}>
                        {issue.severity}
                      </span>
                      <span style={styles.issueDate}>
                        {new Date(issue.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h4 style={styles.issueTitle}>{issue.title}</h4>
                    <div style={styles.issueDetails}>
                      <span style={styles.issueDetail}>
                        <i className="fas fa-server" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.host || issue.ip}
                      </span>
                      {issue.port > 0 && (
                        <span style={styles.issueDetail}>
                          <i className="fas fa-plug" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.port}/{issue.protocol}
                        </span>
                      )}
                      {issue.service && (
                        <span style={styles.issueDetail}>
                          <i className="fas fa-cog" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.service}
                        </span>
                      )}
                    </div>
                    <div style={styles.issueMeta}>
                      <span style={styles.issueMetaItem}>
                        <i className="fas fa-folder" style={{ color: 'var(--color-text-tertiary)' }}></i> {issue.project_name}
                      </span>
                      <Link
                        to={`/scans/${issue.scan_id}`}
                        style={{ ...styles.issueMetaItem, ...styles.issueLink }}
                      >
                        <i className="fas fa-search" style={{ color: 'var(--color-primary-500)' }}></i> {issue.scan_name}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;
